import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import {
  WorkflowsTableFilterArea,
  WorkflowsTableTitleDecorationFilterArea,
  type WorkflowsViewFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas";
import {
  WorkflowsTableFilterSchemaKeys,
  WorkflowsTableFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas/WorkflowsFilterAreas.helpers";
import { getWorkflowListSubjectFilters } from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas/workflowListSubjectFilters.helpers";
import { WorkflowStatusToPillVariantMap } from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useExportList } from "#src/components/hooks/useExportList";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import { linkToAssetDetailPage } from "#src/utils/links";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Pill,
  StorageKeys,
  TextDataDisplay,
} from "@validereinc/common-components";
import {
  AssetType,
  Resources,
  SortDirection,
  WorkflowAdapter,
  WorkflowType,
  type GroupedByEntryType,
} from "@validereinc/domain";
import { type AssetTypeSelectionType } from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import { isToday } from "date-fns";
import React, { useMemo } from "react";

export type WorkflowsTablePanelProps = {
  /** certain columns are turned off when this table panel is used within the workflow template details page */
  isWithinWorkflowTemplateDetails?: boolean;
  /** optionally bypass filters set in session storage which are fetched through useFilters() */
  overrideFilters?: {
    templateId?: string;
  };
} & StorageKeys;

export const WorkflowsTablePanel = ({
  isWithinWorkflowTemplateDetails = false,
  overrideFilters,
  tableConfigStorageKey,
  tableStateStorageKey,
  viewConfigStorageKey,
  filterConfigStorageKey,
  groupByTablesStateStorageKey,
  defaultAssetType,
}: WorkflowsTablePanelProps & {
  defaultAssetType: AssetTypeSelectionType;
}) => {
  const metaHeaders = useDataTableMetaHeaders();
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingCompanyConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [tableFilters] = useStickyState<WorkflowsTableFiltersType>(
    {} as WorkflowsTableFiltersType,
    filterConfigStorageKey
  );
  const [viewFilters] = useStickyState<WorkflowsViewFiltersType>(
    {} as WorkflowsViewFiltersType,
    viewConfigStorageKey
  );
  const { localize } = useLocalization();
  const { categoryId, templateId } = viewFilters;
  const {
    [WorkflowsTableFilterSchemaKeys.date_range]: dateRange,
    [WorkflowsTableFilterSchemaKeys.status]: status,
    [WorkflowsTableFilterSchemaKeys.name]: name,
  } = tableFilters;

  const scopedTemplateId = overrideFilters?.templateId ?? templateId;

  const filters = useMemo(
    () => ({
      created_at: dateRange,
      ...(status ? { status } : {}),
      ...(isWithinWorkflowTemplateDetails
        ? {}
        : { "workflow_category.id": categoryId }),
      ...(scopedTemplateId
        ? isWithinWorkflowTemplateDetails
          ? { workflow_template_id: scopedTemplateId }
          : { "workflow_template.id": scopedTemplateId }
        : {}),
      ...(name ? { name } : {}),
      ...getWorkflowListSubjectFilters(
        {
          ...tableFilters,
          ...viewFilters,
        },
        {
          isWithinWorkflowTemplateDetails,
          defaultAssetType,
        }
      ),
    }),
    [
      dateRange,
      status,
      categoryId,
      scopedTemplateId,
      name,
      tableFilters,
      viewFilters,
      overrideFilters,
      isWithinWorkflowTemplateDetails,
      defaultAssetType,
    ]
  );

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const reqPayload: Parameters<typeof WorkflowAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters,
  };

  const { data, isLoading } = useListWorkflows(reqPayload, {
    enabled:
      !isWithinWorkflowTemplateDetails || Boolean(overrideFilters?.templateId),
  });
  const { data: datasetsData } = useListDatasets();
  const items = data?.data ?? [];

  const datasetById = useMemo(
    () =>
      new Map(
        (datasetsData?.data ?? []).map((dataset) => [dataset.id, dataset.name])
      ),
    [datasetsData]
  );

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...reqPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? reqPayload)
  );

  const workflowsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.WORKFLOW, "getList", d.payload],
      queryFn: () => WorkflowAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const { mutate: handleExport, isLoading: isExporting } = useExportList({
    exportFn: () => WorkflowAdapter.exportList!({ ...reqPayload }),
  });

  const headers = useMemo<Array<HeaderType<WorkflowType>>>(
    () => [
      {
        label: "Name",
        key: "id",
        isSortable: false,
        renderComponent: ({ item }: { item: WorkflowType }) => {
          return (
            <RoutingLink
              to={WorkflowDetailsRoutePath.toLinkParts({
                pathParams: {
                  workflowId: item.id,
                },
              })}
            >
              {item.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={WorkflowStatusToPillVariantMap[item.status] || "default"}
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Due Date",
        key: "due_date",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.due_date}
            withTime={!!(item.due_date && isToday(new Date(item.due_date)))}
            convertToUTC={false}
          />
        ),
      },
      {
        label: "Dismissed At",
        key: "dismissed_at",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.dismissed_at}
            convertToUTC={false}
          />
        ),
      },
      {
        label: "Dismissal Reason",
        key: "dismissal_reason",
        isSortable: false,
        renderComponent: ({ item }) => (
          <TextDataDisplay value={item.dismissal_reason} />
        ),
      },
      {
        label: "Asset",
        isSortable: false,
        key: "asset.name",
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset ? (
            <RoutingLink
              to={linkToAssetDetailPage(item.asset.asset_type, item.asset.id)}
            >
              {item.asset.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        label: "Asset Type",
        isSortable: false,
        key: "asset.type",
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset?.asset_type ? localize(item.asset.asset_type) : "-",
      },
      {
        label: "Dataset",
        isSortable: false,
        key: "dataset_trigger.dataset_id",
        renderComponent: ({ item }: { item: WorkflowType }) => {
          const datasetId = item.dataset_trigger?.dataset_id;
          const datasetName = datasetId
            ? datasetById.get(datasetId)
            : undefined;

          return datasetName ?? "-";
        },
      },
      {
        label: `Associated ${localize("facility")}`,
        isSortable: false,
        key: "facility.id",
        isGroupable: true,
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset &&
          item.asset.asset_type === AssetType.FACILITY &&
          item.facility ? (
            <RoutingLink to={linkToFacilityDetail(item.facility_id ?? "")}>
              {item.facility.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      ...(!isWithinWorkflowTemplateDetails
        ? [
            {
              label: "Category",
              key: "workflow_template.workflow_category.name",
              isSortable: false,
              renderComponent: ({ item }: { item: WorkflowType }) => (
                <RoutingLink
                  to={WorkflowCategoryDetailsRoutePath.toLinkParts({
                    pathParams: {
                      workflowCategoryId:
                        item.workflow_template.workflow_category.id,
                    },
                  })}
                >
                  {item.workflow_template.workflow_category.name}
                </RoutingLink>
              ),
            },
            {
              label: "Template",
              key: "workflow_template_id",
              isSortable: false,
              isGroupable: true,
              groupByKeys: ["workflow_template_id", "workflow_template.name"],
              groupBySortByKey: "workflow_template.name",
              renderComponent: ({ item }: { item: WorkflowType }) =>
                item.workflow_template?.name ? (
                  <RoutingLink
                    to={WorkflowTemplateDetailsRoutePath.toLinkParts({
                      pathParams: {
                        workflowTemplateId: item.workflow_template_id,
                      },
                    })}
                  >
                    {item.workflow_template.name}
                  </RoutingLink>
                ) : (
                  "-"
                ),
            },
          ]
        : []),
      ...metaHeaders,
    ],
    [datasetById, isWithinWorkflowTemplateDetails, localize, metaHeaders]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: workflowsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Workflows list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingCompanyConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  const actionRow = (
    <PermissionAwareExportButton
      key="export-workflows"
      onClick={handleExport}
      isExporting={isExporting}
      isDisabled={tableState.groupBy.length > 0}
    />
  );

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Workflows",
        isFluidY: false,
        actionRow,
        titleDecorator: (
          <WorkflowsTableTitleDecorationFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        ),
      }}
      filterComponent={
        <WorkflowsTableFilterArea
          viewConfigStorageKey={viewConfigStorageKey}
          filterConfigStorageKey={filterConfigStorageKey}
          defaultAssetType={defaultAssetType}
        />
      }
      columnConfigurationProps={columnConfigurationDataTableProps}
      dataTableProps={{
        intent: "simplicity-first",
        headers,
        items: data?.data ?? [],
        getItemId: (item) => item.id,
        isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: data?.total_entries,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
      {...groupByDataTableProps}
    />
  );
};
